/**
 * EPL Admin Portal - Layout Principal
 * Layout con sidebar de navegación para el portal de administración
 */

import React, { useState } from 'react';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  LayoutDashboard, 
  Users, 
  FlaskConical, 
  BarChart3, 
  Brain, 
  FileText, 
  Settings, 
  Shield,
  ChevronLeft,
  Bell,
  Menu,
  ArrowLeft
} from 'lucide-react';

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const menuItems = [
    { 
      id: 'dashboard', 
      label: 'Dashboard', 
      icon: LayoutDashboard, 
      path: '/epl/admin/dashboard' 
    },
    { 
      id: 'users', 
      label: 'Usuarios', 
      icon: Users, 
      path: '/epl/admin/users' 
    },
    { 
      id: 'labs', 
      label: 'Laboratorios', 
      icon: FlaskConical, 
      path: '/epl/admin/labs' 
    },
    { 
      id: 'results', 
      label: 'Resultados', 
      icon: BarChart3, 
      path: '/epl/admin/results' 
    },
    { 
      id: 'ai-analytics', 
      label: 'IA Analytics', 
      icon: Brain, 
      path: '/epl/admin/ai-analytics' 
    },
    { 
      id: 'reports', 
      label: 'Reportes', 
      icon: FileText, 
      path: '/epl/admin/reports' 
    },
    { 
      id: 'settings', 
      label: 'Configuración', 
      icon: Settings, 
      path: '/epl/admin/settings' 
    },
    { 
      id: 'audit', 
      label: 'Auditoría', 
      icon: Shield, 
      path: '/epl/admin/audit' 
    }
  ];

  const handleNavigate = (path) => {
    navigate(path);
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <aside 
        className={`
          bg-white border-r border-gray-200 transition-all duration-300 flex flex-col
          ${sidebarCollapsed ? 'w-20' : 'w-64'}
        `}
      >
        {/* Header */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-gray-200">
          {!sidebarCollapsed && (
            <div className="flex items-center gap-2">
              <FlaskConical className="w-6 h-6 text-blue-600" />
              <span className="font-semibold text-gray-900">EPL Admin</span>
            </div>
          )}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <ChevronLeft 
              className={`w-5 h-5 text-gray-600 transition-transform ${
                sidebarCollapsed ? 'rotate-180' : ''
              }`}
            />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4">
          <ul className="space-y-1 px-3">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              
              return (
                <li key={item.id}>
                  <button
                    onClick={() => handleNavigate(item.path)}
                    className={`
                      w-full flex items-center gap-3 px-3 py-2.5 rounded-lg
                      transition-colors text-sm font-medium
                      ${active 
                        ? 'bg-blue-50 text-blue-700' 
                        : 'text-gray-700 hover:bg-gray-100'
                      }
                    `}
                    title={sidebarCollapsed ? item.label : ''}
                  >
                    <Icon className={`w-5 h-5 ${active ? 'text-blue-600' : 'text-gray-500'}`} />
                    {!sidebarCollapsed && (
                      <span>{item.label}</span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200">
          <button
            onClick={() => navigate('/dashboard')}
            className={`
              w-full flex items-center gap-3 px-3 py-2.5 rounded-lg
              transition-colors text-sm font-medium text-gray-700 hover:bg-gray-100
            `}
          >
            <ArrowLeft className="w-5 h-5 text-gray-500" />
            {!sidebarCollapsed && <span>Volver</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header Azul con Logo EduTech */}
        <header className="h-16 bg-gradient-to-r from-blue-700 to-blue-900 shadow-lg flex items-center justify-between px-6">
          {/* Logo y Título */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="md:hidden p-2 rounded-lg hover:bg-blue-800 transition-colors"
            >
              <Menu className="w-5 h-5 text-white" />
            </button>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center">
                <span className="text-blue-700 font-bold text-lg">E</span>
              </div>
              <div>
                <h1 className="text-white font-bold text-lg">Edutech Pulse Lab</h1>
                <p className="text-blue-200 text-xs">Portal de Administración</p>
              </div>
            </div>
          </div>
          
          {/* Usuario y Notificaciones */}
          <div className="flex items-center gap-4">
            <button className="relative p-2 rounded-lg hover:bg-blue-800 transition-colors">
              <Bell className="w-5 h-5 text-white" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
            </button>
            
            <div className="flex items-center gap-3">
              <div className="text-right">
                <p className="text-sm font-medium text-white">{user?.nombre || user?.name || 'Admin'}</p>
                <p className="text-xs text-blue-200">{user?.role === 'admin' ? 'Administrador' : user?.email}</p>
              </div>
              <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center ring-2 ring-white">
                <span className="text-white text-sm font-medium">
                  {(user?.nombre || user?.name || 'A').charAt(0).toUpperCase()}
                </span>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto bg-gray-50">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
